import { renderNavbar } from '../components/navbar.js';
import { supabase, getCurrentUser, isSupabaseReady } from '../services/supabase.js';
import { escapeHtml } from '../utils/html.js';
import {
  compactAiAnswer,
  formatQuizClosingDate,
  formatQuizTimeRemaining,
  isChallengeRevealed,
  quizFrames
} from '../utils/quiz.js';
import { scoreQuizAnswer } from '../utils/quiz-scoring.js';
import { tr } from '../services/i18n.js';

export async function renderQuiz(container) {
  container.innerHTML = `
    <div class="page p-16" style="padding-bottom:100px;">
      <div class="app-header">
        <div class="brand">
          <div class="brand-mark">FV</div>
          <div>
            <p class="brand-title">${tr('Quiz tactiques', 'Tactical quizzes')}</p>
            <p class="brand-subtitle">${tr('Analyse avant de voir la réponse IA', 'Analyze before seeing the AI answer')}</p>
          </div>
        </div>
        <a class="btn-secondary" href="#/home">Feed</a>
      </div>
      <div id="quiz-content">
        <div class="glass-card skeleton" style="height:220px;"></div>
      </div>
    </div>
  `;
  renderNavbar(container);

  const content = container.querySelector('#quiz-content');
  if (!isSupabaseReady()) {
    content.innerHTML = `<div class="glass-card"><p class="text-muted">${tr('Supabase non configuré.', 'Supabase is not configured.')}</p></div>`;
    return;
  }

  const user = await getCurrentUser();
  try {
    const { data: challengeRows, error } = await supabase
      .from('match_challenges_public')
      .select('id,creator_id,competition_id,match_id,source_debrief_id,title,prompt,frame_previews,ai_answer,reveal_at,status,created_at')
      .order('created_at', { ascending: false })
      .limit(20);
    if (error) throw error;

    const challenges = await enrichChallenges(challengeRows || []);

    if (!challenges?.length) {
      content.innerHTML = `
        <div class="glass-card">
          <p style="font-weight:700;margin-bottom:8px;">${tr('Aucun quiz actif', 'No active quiz')}</p>
          <p class="text-muted" style="margin:0;">${tr('Crée un quiz depuis un débrief contenant des images clés.', 'Create a quiz from a debrief with key frames.')}</p>
        </div>`;
      return;
    }

    const entriesByChallenge = await loadEntriesByChallenge(challenges);
    const ownEntryMap = {};
    Object.values(entriesByChallenge).flat().forEach(e => {
      if (user && e.user_id === user.id) ownEntryMap[e.challenge_id] = e;
    });

    content.innerHTML = `<div class="flex-col gap-16">${challenges.map(c => renderChallenge(c, ownEntryMap[c.id], entriesByChallenge[c.id] || [], !!user)).join('')}</div>`;
    startQuizCountdowns(content);
    content.querySelectorAll('[data-quiz-submit]').forEach(btn => {
      btn.addEventListener('click', async () => {
        const id = btn.dataset.quizSubmit;
        const challenge = challenges.find((c) => c.id === id);
        if (isChallengeRevealed(challenge)) {
          showToast(tr('Ce quiz est clôturé.', 'This quiz is closed.'));
          renderQuiz(container);
          return;
        }
        const textarea = content.querySelector(`[data-quiz-answer="${id}"]`);
        const answer = textarea?.value.trim();
        if (!answer) return showToast(tr('Écris ton analyse avant de valider.', 'Write your analysis before submitting.'));
        btn.disabled = true;
        const { error: upsertError } = await supabase.from('quiz_entries').upsert({
          challenge_id: id,
          user_id: user.id,
          user_answer: answer
        }, { onConflict: 'challenge_id,user_id' });
        if (upsertError) {
          btn.disabled = false;
          showToast(tr('Quiz indisponible : migration Supabase à appliquer.', 'Quiz unavailable: apply the Supabase migration.'));
          return;
        }
        showToast(tr('Analyse envoyée. Réponse IA masquée jusqu’à la révélation.', 'Analysis sent. AI answer hidden until reveal.'));
        setTimeout(() => renderQuiz(container), 500);
      });
    });
  } catch (e) {
    content.innerHTML = `
      <div class="glass-card">
        <p style="font-weight:700;margin-bottom:8px;">${tr('Quiz indisponibles', 'Quizzes unavailable')}</p>
        <p class="text-muted" style="margin:0;">${tr('Applique la migration Supabase pour activer les défis quiz.', 'Apply the Supabase migration to enable quiz challenges.')}</p>
      </div>`;
  }
}

async function enrichChallenges(challenges) {
  if (!challenges.length) return [];
  const creatorIds = [...new Set(challenges.map((c) => c.creator_id).filter(Boolean))];
  const matchIds = [...new Set(challenges.map((c) => c.match_id).filter(Boolean))];
  const competitionIds = [...new Set(challenges.map((c) => c.competition_id).filter(Boolean))];

  const [profiles, matches, competitions] = await Promise.all([
    loadByIds('profiles', creatorIds, 'id,pseudo'),
    loadByIds('matches', matchIds, 'id,home_team,away_team,kickoff_at'),
    loadByIds('competitions', competitionIds, 'id,name')
  ]);

  const profileById = new Map(profiles.map((row) => [row.id, row]));
  const matchById = new Map(matches.map((row) => [row.id, row]));
  const competitionById = new Map(competitions.map((row) => [row.id, row]));

  return challenges.map((challenge) => ({
    ...challenge,
    creator: profileById.get(challenge.creator_id) || null,
    match: matchById.get(challenge.match_id) || null,
    competition: competitionById.get(challenge.competition_id) || null
  }));
}

async function loadByIds(table, ids, columns) {
  if (!ids.length) return [];
  const { data, error } = await supabase
    .from(table)
    .select(columns)
    .in('id', ids);
  if (error) return [];
  return data || [];
}

async function loadEntriesByChallenge(challenges) {
  const ids = challenges.map(c => c.id);
  if (!ids.length) return {};
  let entries = [];
  const rich = await supabase
    .from('quiz_entries')
    .select('*, user:profiles(pseudo, avatar_url, avatar_emoji)')
    .in('challenge_id', ids);
  if (rich.error) {
    const fallback = await supabase
      .from('quiz_entries')
      .select('*')
      .in('challenge_id', ids);
    entries = fallback.data || [];
  } else {
    entries = rich.data || [];
  }
  return entries.reduce((acc, entry) => {
    (acc[entry.challenge_id] ||= []).push(entry);
    return acc;
  }, {});
}

function renderChallenge(challenge, entry, entries, canSubmit) {
  const revealed = isChallengeRevealed(challenge);
  const frames = quizFrames(challenge, 4);
  const closeAt = formatQuizClosingDate(challenge);
  const timeRemaining = formatQuizTimeRemaining(challenge);
  return `
    <article class="glass-card">
      <div style="display:flex;justify-content:space-between;gap:10px;margin-bottom:12px;align-items:flex-start;">
        <div>
          <p class="eyebrow">${escapeHtml(challenge.competition?.name || 'Quiz')}</p>
          <h2 class="heading-2" style="margin:0;">${escapeHtml(challenge.title || tr('Défi tactique', 'Tactical challenge'))}</h2>
        </div>
        <span class="text-muted" style="font-size:12px;">${revealed ? tr('Clôturé', 'Closed') : tr('Ouvert', 'Open')}</span>
      </div>
      <div style="display:grid;gap:6px;padding:10px 12px;border:1px solid var(--line);border-radius:8px;background:var(--surface-2);margin-bottom:12px;">
        <p style="margin:0;font-weight:750;">${tr('Clôture', 'Closes')}: ${escapeHtml(closeAt)}</p>
        <p class="text-muted" data-quiz-countdown="${escapeHtml(challenge.id)}" data-quiz-reveal-at="${escapeHtml(challenge.reveal_at || '')}" style="margin:0;font-size:13px;">${escapeHtml(timeRemaining)}</p>
      </div>
      <p class="text-muted" style="margin:0 0 12px;">${escapeHtml(challenge.prompt || tr('Analyse cette séquence.', 'Analyze this sequence.'))}</p>
      <div style="display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:8px;margin-bottom:12px;">
        ${frames.length ? frames.map(src => `<img src="${src}" alt="" style="width:100%;border-radius:8px;border:1px solid var(--line);" />`).join('') : `<p class="text-muted">${tr('Images indisponibles', 'Images unavailable')}</p>`}
      </div>
      ${entry ? `<div style="padding:10px;background:var(--surface-2);border-radius:8px;margin-bottom:12px;"><p class="text-muted" style="font-size:12px;margin:0 0 4px;">${tr('Ton analyse', 'Your analysis')}</p><p style="margin:0;">${escapeHtml(entry.user_answer)}</p></div>` : ''}
      ${canSubmit && !revealed ? `
        <textarea data-quiz-answer="${escapeHtml(challenge.id)}" class="input-glow" rows="4" style="width:100%;resize:vertical;" placeholder="${tr('Décris possession, espaces, pressing, décision clé...', 'Describe possession, spaces, pressing, key decision...')}">${entry ? escapeHtml(entry.user_answer) : ''}</textarea>
        <button data-quiz-submit="${escapeHtml(challenge.id)}" class="btn-primary" style="width:100%;margin-top:10px;">${tr('Valider mon analyse', 'Submit my analysis')}</button>
      ` : ''}
      ${revealed ? `
        <div style="padding:12px;background:var(--surface-2);border-radius:8px;margin-top:12px;">
          <p style="font-weight:700;margin:0 0 6px;">${tr('Réponse IA', 'AI answer')}</p>
          <p style="white-space:pre-wrap;margin:0;font-size:14px;line-height:1.5;">${escapeHtml(compactAiAnswer(challenge.ai_answer || {}))}</p>
        </div>
        ${renderQuizRanking(entries, challenge.ai_answer)}
      ` : `<p class="text-muted" style="font-size:12px;margin:10px 0 0;">${tr('La réponse IA complète sera révélée à la clôture du quiz.', 'The full AI answer will be revealed when the quiz closes.')}</p>`}
    </article>
  `;
}

function startQuizCountdowns(content) {
  if (window.__footDebriefQuizCountdownTimer) {
    clearInterval(window.__footDebriefQuizCountdownTimer);
  }

  const update = () => {
    if (!content.isConnected) {
      clearInterval(window.__footDebriefQuizCountdownTimer);
      window.__footDebriefQuizCountdownTimer = null;
      return;
    }
    const now = Date.now();
    content.querySelectorAll('[data-quiz-reveal-at]').forEach((node) => {
      const revealAt = node.getAttribute('data-quiz-reveal-at');
      node.textContent = formatQuizTimeRemaining({ reveal_at: revealAt }, now);
    });
  };

  update();
  window.__footDebriefQuizCountdownTimer = setInterval(update, 30000);
}

function renderQuizRanking(entries, aiAnswer) {
  const ranked = (entries || [])
    .map((entry) => ({ entry, result: scoreQuizAnswer(entry.user_answer, aiAnswer) }))
    .sort((a, b) => b.result.score - a.result.score);
  if (!ranked.length) {
    return `<p class="text-muted" style="font-size:12px;margin:12px 0 0;">${tr('Aucune participation à classer.', 'No entries to rank yet.')}</p>`;
  }
  return `
    <div style="margin-top:12px;">
      <p style="font-weight:800;margin:0 0 8px;">${tr('Classement proximité IA', 'AI proximity ranking')}</p>
      <div class="flex-col gap-8">
        ${ranked.map(({ entry, result }, index) => `
          <div style="display:grid;grid-template-columns:34px minmax(0,1fr) auto;gap:10px;align-items:center;padding:10px;border:1px solid var(--line);border-radius:8px;background:var(--surface);">
            <strong>#${index + 1}</strong>
            <div style="min-width:0;">
              <p style="margin:0;font-weight:750;">${escapeHtml(entry.user?.pseudo || tr('Joueur', 'Player'))}</p>
              <p class="text-muted" style="margin:3px 0 0;font-size:12px;">
                ${escapeHtml(result.verdict)}
                ${result.matchedKeywords.length ? ` · ${escapeHtml(result.matchedKeywords.slice(0, 4).join(', '))}` : ''}
              </p>
            </div>
            <strong style="color:var(--primary);">${result.score} pts</strong>
          </div>
        `).join('')}
      </div>
    </div>
  `;
}

function showToast(msg) {
  const t = document.createElement('div');
  t.className = 'toast show';
  t.textContent = msg;
  document.body.appendChild(t);
  setTimeout(() => { t.className = 'toast'; setTimeout(() => t.remove(), 300); }, 3000);
}
